<template>
    <modal :size="step==2 ? 'lg' : 'md'" @close="() => $emit('close')" :open="localOpen">
        <template v-if="step == 3" #header>
            Thank You!
        </template>
        <template v-else-if="property" #header>
            Inquire about this property
        </template>
        <template v-else #header>
            Prefer a more personalized approach?
        </template>
        <template v-if="step == 3" #default>
            <p>We received your request and will contact you shortly</p>
        </template>
        <template v-else #default>
            <template v-if="step == 1">
                <p v-if="property">
                    Enter your phone number to start a conversation with the host
                </p>
                <p v-else>
                    Enter your phone number to chat with a representative
                </p>
            </template>
            <template v-else>
                <h3 v-if="property">{{ property.name }}</h3>
                <p v-else>Please complete the information below and our Travel Specialist team will send you best available rental options:</p>
            </template>

            <transition name="v-slide-transition-7">

                <phone-cta v-if="step==1" type="inquiry" :pageLocation="property ? 'view-property' : 'personalized-approach'" :followUp="false" @submit="goToStep2"/>
                
                <form v-else @submit.prevent="processForm" :id="formId">
                    <container>
                        <row>
                            <column :md="12">
                                <form-group :valid="firstNameValid">
                                    <form-label>First Name:*</form-label>
                                    <form-input name="first-name" v-model="firstName" @input="validateForm" />
                                </form-group>
                            </column>
                            <column :md="12">
                                <form-group :valid="lastNameValid">
                                    <form-label>Last Name:*</form-label>
                                    <form-input name="last-name" v-model="lastName" @input="validateForm" />
                                </form-group>
                            </column>
                            <column :md="12">
                                <form-group :valid="emailValid">
                                    <form-label>Email:*</form-label>
                                    <form-input type="email" name="email" v-model="email" @input="validateForm" />
                                </form-group>
                            </column>
                            <column :lg="12">
                                <div class="small float-end text-muted">
                                    <form-checkbox name="dates-flexible" v-model="flexible" :switch="true" @input="validateForm">
                                        Dates are flexible
                                    </form-checkbox>
                                </div>
                                <form-group>
                                    
                                    <form-label>Tentative Travel Dates:</form-label>
                                    
                                    <single-range-calendar v-model="dateRange" @input="validateForm" :circleWedges="true" title="Travel Dates" startLabel='Check-in' startDateInstructions='Select Check-in Date' endLabel='Check-out' endDateInstructions='Select Check-out Date' min="today" />
                                </form-group>
                            </column>
                            <column :md="12" :lg="6">
                                <form-group>
                                    <form-label>Number of Adults:</form-label>
                                    <form-input type="number" name="adults-count" v-model="adults" @input="validateForm" />
                                </form-group>
                            </column>
                            <column :md="12" :lg="6">
                                <form-group>
                                    <form-label>Number of Children:</form-label>
                                    <form-input type="number" name="children-count" v-model="children" @input="validateForm" />
                                </form-group>
                            </column>
                            <column :lg="12">
                                <form-group>
                                    <form-label>How can we help you?</form-label>
                                    <form-input  name="message" v-model="message" :lines="2" @input="validateForm" />
                                </form-group>
                            </column>
                        </row>
                    </container>

                    
                </form>
            </transition>
            
        </template>
        <template v-if="step==2" #buttons>
            <ui-button @click="step=1" :underline="true" class="order-2 order-md-1 me-auto" block="sm" :loading="buttonsLoading" :disabled="buttonsLoading">
                Back
            </ui-button>
            <ui-button color="secondary" @click="localOpen=false" :underline="true" class="order-3 order-md-2" block="sm" :loading="buttonsLoading" :disabled="buttonsLoading">
                Close
            </ui-button>
            <ui-button :submit="true" :form="formId" class="order-1 order-md-3 mb-3 mb-md-0" block="sm" :loading="buttonsLoading" :disabled="buttonsLoading">
                Submit
            </ui-button>
        </template>
        <template v-else-if="step==3" #buttons>
            <ui-button color="secondary" @click="localOpen=false" :underline="true" block="sm">
                Close
            </ui-button>
        </template>
    </modal>
</template>

<script type="text/javascript">
    import CommonComponents from '@/components/Common.vue';
    import ApiObject from '@/classes/ApiObject.js';
    import Api from '@/classes/Api.js';
    import DateObject from '@/classes/DateObject.js';
    import DateRange from '@/classes/Ranges/Date.js';
    import Analytics from '@/classes/Analytics.js';
    import PhoneCta from '@/components/Content/PhoneCta.vue';

    import { UseUIStore } from '@/store/UI.js';
    import { UseAuthStore } from '@/store/Auth.js';

    export default {
        components: {
            PhoneCta,
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
                authStore: UseAuthStore(),
            };
        },

        data() {
            return {
                step: 1,
                localOpen: true,

                firstName: null,
                lastName: null,
                email: null,
                dateRange: null,
                flexible: false,
                adults: null,
                children: null,
                message: null,

                buttonsLoading: false,

                startValidation: false,

                firstNameValid: null,
                lastNameValid: null,
                emailValid: null,

                formId: null,
                ctaId: null,
                ctaToken: null,
            };
        },

        props: {
            property: {
                type: ApiObject,
            },

            open: {
                type: Boolean,
                default: true,
            },

            checkIn: {
                type: DateObject,
            },

            checkOut: {
                type: DateObject,
            },

            numAdults: {
                type: Number,
            },

            numChildren: {
                type: Number,
            },

            inquireType: {
                type: String,
            },
        },

        emits: ['close', 'inquire'],

        created() {
            this.localOpen = this.open;
            if ((this.checkIn) && (this.checkOut)) {
                this.dateRange = new DateRange(this.checkIn, this.checkOut);
            }

            if ((this.numAdults) && (this.numAdults > 0)) {
                this.adults = this.numAdults;
            }

            if ((this.numChildren) && (this.numChildren > 0)) {
                this.children = this.numChildren;
            }

            if (this.authStore.loggedInUser) {
                this.firstName = this.authStore.loggedInUser.firstName;
                this.lastName = this.authStore.loggedInUser.lastName;
                this.email = this.authStore.loggedInUser.email;
            }

            if (this.property) {
                this.formId = `property${this.property.id}InquireForm`;
            }
            else {
                this.formId = 'personalTouch';
                if (this.inquireType) {
                    this.formId = this.inquireType+'PersonalTouch'
                }
            }
        },

        watch: {
            open(newVal) {
                this.localOpen = newVal;
            },
        },

        methods: {
            goToStep2(data) {
                if (
                    (data) &&
                    (data.id)
                ) {
                    this.ctaId = data.id;
                    this.ctaToken = data.token;

                    this.step = 2;
                }
                else {
                    this.$emit('inquire');
                    this.step = 3;
                }
            },

            validateForm() {
                if (!this.startValidation) return;

                let valid = true;
                this.firstNameValid = null;
                this.lastNameValid = null;
                this.emailValid = null;

                if (!this.firstName) {
                    valid = false;
                    this.firstNameValid = false;
                }

                if (!this.lastName) {
                    valid = false;
                    this.lastNameValid = false;
                }

                if (!this.email) {
                    valid = false;
                    this.emailValid = false;
                }

                return valid;

            },

            async processForm() {
                this.startValidation = true;

                if (!this.validateForm()) {
                    this.uiStore.toast({
                        error: true,
                        title: 'Required Fields',
                        message: 'Please complete the missing highlighted fields',
                    });

                    return;
                }

                this.buttonsLoading = true;

                let ctaData = {
                    checkIn: this.dateRange ? this.dateRange.start.format('Y-m-d') : null,
                    checkOut: this.dateRange ? this.dateRange.end.format('Y-m-d') : null,
                    flexible: this.flexible,
                    adults: this.adults,
                    children: this.children,
                    message: this.message,
                    inquireType: this.inquireType,
                    thirdParty: this.$route.params.thirdParty,
                };

                let data = {
                    ctaId: this.ctaId,
                    ctaToken: this.ctaToken,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    inquireType: this.property ? 'property' : 'personalTouch',
                    data: ctaData,
                };

                if (this.property) {
                    data.propertyId = this.property.id;
                }

                let response = await Api.post('leads/start-conversation-followup', data);

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    if (this.property) {
                        Analytics.event('Lead', {
                            content_category: 'property',
                            content_name: this.property.name,
                            content: [{id: this.property.id, quantity:1}],
                        });
                    }
                    else {
                        Analytics.event('Lead', {
                            content_category: 'property',
                            content_name: 'Personal Touch',
                        });
                    }
                    
                    this.$emit('inquire');
                    this.step = 3;

                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {

                        if (response.results.error.message) {
                            error = response.results.error.message;
                        }
                        else {
                            error = response.results.error;
                        }
                    }

                    this.uiStore.toast({
                        error: true,
                        title: 'Error',
                        message: error,
                    });
                }

                this.buttonsLoading = false;
            },
        }
    };

</script>
